import React from 'react';
import { Box, HorizontalScroll } from '@drone-js/core';
import { Loader } from 'component/common/Loader/Loader';
import { useSelector } from 'react-redux';
import { measureFinishRender } from 'core/Performance/PerformanceService';
import { selectPageTypeId } from 'store/slice/navigation';
import { selectActivityImagesFromDetailState } from 'component/page/detail/store/detail.selectors';
import 'component/page/detail/DetailCard/ActivityCarousel/ActivityCarousel.scss';
import { selectCmsFromBootstrap } from 'store/slice/bootstrap';
import { StringFormatter } from '@agoda/react-handyman';
import { ActivitiesImageCarouselSeenEventManifestV2 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesImageCarouselSeenEventFieldsV2 } from '@agoda/analytics-messages-definition-activities/events.split';
import { selectScreenSize } from 'store/slice/screen';
import ImageContainer from 'component/page/detail/DetailCard/ActivityCarousel/ImageContainer';

interface Props {
    onImageClick: (e: React.MouseEvent, index: number, isViewAllImages: boolean) => void;
}

const getImageSize = (isMobileScreenSize: boolean) => {
    if (isMobileScreenSize) {
        return '220px';
    }
    return '320px';
};

const ActivityCarousel: React.FC<Props> = React.memo((props): React.JSX.Element => {
    const { onImageClick } = props;
    const [isLoadFinished, setIsLoadFinished] = React.useState(false);
    const images = useSelector(selectActivityImagesFromDetailState);
    const cms = useSelector(selectCmsFromBootstrap);
    const pageTypeId = useSelector(selectPageTypeId);
    const screenSize = useSelector(selectScreenSize);
    const imageHeight = getImageSize(screenSize.mobile);

    const createOnLoadOnFirstImage = (index: number) => {
        if (index === 0) {
            return () => {
                setIsLoadFinished(true);
                measureFinishRender({ component: 'CarouselComponent' }, pageTypeId);
            };
        }
        return undefined;
    };

    const onSeen = (index: number) => (inView: boolean) => {
        if (!inView) return;
        trackElementEvent<ActivitiesImageCarouselSeenEventFieldsV2>(ActivitiesImageCarouselSeenEventManifestV2.ids, {}, { action_element_index: index + 1 });
    };

    if (!images || images.length === 0) {
        return <Loader element="detail-image-carousel" />;
    }

    return (
        <Box mx="0" height={imageHeight}>
            <div
                data-element-name="activities-image-carousel"
                data-testid="activities-image-carousel"
                className={`ActivityCarouselRevamp set-min-width ${isLoadFinished ? 'ActivityCarouselRevamp--loaded' : ''}`}
            >
                <HorizontalScroll columnGap="2" nextLabel={cms[130706]} previousLabel={cms[130765]} behavior="item" width="100%">
                    {images.map((image, index) => (
                        <ImageContainer
                            key={`activities-image-carousel-${index}`}
                            index={index}
                            image={image}
                            altText={StringFormatter.format(cms[321481], index, images.length)}
                            onSeen={onSeen(index)}
                            onLoad={createOnLoadOnFirstImage(index)}
                            imageCount={images.length}
                            imageHeight={imageHeight}
                            onClick={(e) => onImageClick(e, index, false)}
                        />
                    ))}
                </HorizontalScroll>
            </div>
        </Box>
    );
});
ActivityCarousel.displayName = 'ActivityCarousel';

export default ActivityCarousel;
